import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import TabBar from '../../../../component/tab-bar/tab-bar';
import { CrmAccountEntity } from '../../../../api/types/crm/accounts/crm-account-entity';
import { AccountContacts } from './tabs/contacts/account-contacts';
import { AccountPayments } from './tabs/payments/account-payments';
import { AccountBills } from './tabs/bills/account-bills';
import { CrmAccountDetailsResponse } from '../../../../api/types/crm/accounts/crm-account-details-response';
import { getCrmAccountDetailsOperation } from './operations/get-crm-account-details-operation';

interface CrmAccountDetailsDialogProps {
  visible: boolean;
  account: CrmAccountEntity | null;
  onHide: () => void;
}

export const CrmAccountDetailsDialog: React.FC<
  CrmAccountDetailsDialogProps
> = ({ visible, account, onHide }) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [details, setDetails] = useState<CrmAccountDetailsResponse | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const tabs = ['אנשי קשר', 'תשלומים', 'חשבונות'];

  const accountId = account?.id;

  const refresh = async () => {
    if (!accountId) return;
    await getCrmAccountDetailsOperation(accountId, setDetails, setLoading);
  };

  useEffect(() => {
    if (visible && accountId) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, accountId]);

  const pages = useMemo(
    () => [
      <AccountContacts
        accountId={accountId ?? null}
        contacts={details?.contacts ?? []}
        loading={loading}
        onRefresh={refresh}
      />,
      <AccountPayments
        accountId={accountId ?? null}
        payments={details?.payments ?? []}
        loading={loading}
        onRefresh={refresh}
        creditCardTokens={details?.accountTokens ?? []}
        defaultReceiptRecipientName={account?.accountName?.trim() ?? ''}
        defaultRegistrationNumber={account?.registrationNumber?.trim() ?? ''}
        defaultPhoneNumber={
          account?.phone ?? details?.contacts?.[0]?.phone?.trim() ?? ''
        }
      />,
      <AccountBills
        accountId={accountId ?? null}
        bills={details?.bills ?? []}
        loading={loading}
        onRefresh={refresh}
        defaultReceiptRecipientName={account?.accountName?.trim() ?? ''}
      />,
    ],
    [
      accountId,
      account?.accountName,
      account?.registrationNumber,
      details?.contacts,
      details?.payments,
      details?.bills,
      loading,
    ],
  );

  if (!visible) return null;

  return (
    <Dialog
      header={account ? `חשבון לקוח - ${account.accountName}` : 'חשבון לקוח'}
      visible={visible}
      onHide={onHide}
      style={{ width: '900px', maxWidth: '95vw', direction: 'rtl' }}
      modal
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            label="סגירה"
            icon="pi pi-times"
            severity="secondary"
            text
            onClick={onHide}
          />
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <TabBar
          tabs={tabs}
          onClick={(index) => {
            setSelectedIndex(index);
          }}
        />
        <div>{pages[selectedIndex]}</div>
      </div>
    </Dialog>
  );
};
